import { Name, formatDate, formatName, formatTime } from "@certn/utils";
import { css } from "@emotion/css";
import React, { Key } from "react";

import {
  Avatar,
  DefaultTag,
  GhostIconButton,
  Icon,
  NeutralTag,
  NewTag,
  Text,
} from "@certn/cascadia-react";

import { DropdownMenu } from "../../components/dropdown-menu/DropdownMenu";

interface CommentsProps {
  name: Name;
  roles?: {
    id: string;
    name: string;
  }[];
  dateAdded: string;
  commentContent: string;
  commentLeading?: string;
  containerWidth?: "narrow" | "default";
  verifiedRoles?: string[];
  menuItems?: React.ReactElement | React.ReactElement[] | null;
  className?: string;
  commentVisibility?: "INTERNAL" | "EXTERNAL" | "REPORT";
  onMenuItemPress?: (key: Key) => void;
  editedLabel?: string;
}
const getVisibilityTag = (visibility: "INTERNAL" | "EXTERNAL" | "REPORT") => {
  switch (visibility) {
    case "EXTERNAL":
      return <NewTag shape="pill">External</NewTag>;
    case "INTERNAL":
      return <NeutralTag shape="pill">Internal</NeutralTag>;
    case "REPORT":
      return <DefaultTag shape="pill">Report</DefaultTag>;
  }
};

export function Comments(props: CommentsProps) {
  const {
    name,
    roles,
    dateAdded,
    commentContent,
    commentLeading,
    containerWidth = "default",
    verifiedRoles = ["General - System Admin", "Fulfillment Manager"],
    menuItems,
    commentVisibility,
    editedLabel,
  } = props;
  const isVerified =
    roles && roles.some((role) => verifiedRoles.includes(role.name));
  const avatar = <Avatar name={name} size="small" color="purple" />;
  return (
    <div
      className={css(
        props.className,
        containerWidth === "narrow" && { width: "463px" }
      )}
      data-testid="comment"
    >
      <div className={styles.contentWrapper}>
        <div className={styles.headerBar}>
          <div className={styles.headerLeft}>
            <div>{avatar}</div>
            <div className={styles.titleDateWrapper}>
              <div className={styles.titleWrapper}>
                <Text
                  truncate
                  style="global/heading-small"
                  color="text-base"
                  testID="commenter-name"
                >
                  {formatName(name)}
                </Text>
                {commentVisibility && getVisibilityTag(commentVisibility)}
                {isVerified && (
                  // TODO: Add the tooltip when that has been created: https://certn.atlassian.net/browse/CDS-96
                  <Icon
                    name="new_releases"
                    color="icon-accent-green"
                    type="mini"
                  />
                )}
              </div>
              <div>
                <Text
                  style="global/info-label"
                  color="text-weak"
                  testID="comment-timestamp"
                >
                  {formatDate(dateAdded, "PPP")}
                  {" at "}
                  {formatTime(dateAdded, "p")}
                  {editedLabel ? ` · ${editedLabel}` : null}
                </Text>
              </div>
            </div>
          </div>
          {menuItems && (
            <div className={styles.headerRight}>
              <div>
                <DropdownMenu
                  placement="bottom end"
                  onItemAction={props.onMenuItemPress}
                  triggerButton={
                    <GhostIconButton
                      size="small"
                      aria-label="AriaMenu"
                      icon="more_vert"
                      testID="comments-actions-menu"
                    />
                  }
                >
                  {menuItems}
                </DropdownMenu>
              </div>
            </div>
          )}
        </div>

        <div className={styles.commentContent}>
          {commentLeading && (
            <Text
              style="global/heading-small"
              color="text-base"
              testID="comment-text"
            >
              {commentLeading}{" "}
            </Text>
          )}
          <Text
            style="global/body-secondary"
            color="text-base"
            testID="comment-text"
          >
            {commentContent}
          </Text>
        </div>
      </div>
    </div>
  );
}

const styles = {
  headerBar: css({
    display: "flex",
    gap: "var(--space-2)",
    alignItems: "flex-start",
    flexShrink: 0,
    alignSelf: "stretch",
  }),
  headerLeft: css({
    justifyContent: "flex-start",
    alignItems: "center",
    padding: "var(--space-0)",
    display: "flex",
    flexGrow: 1,
    flexShrink: 0,
    gap: "var(--space-2)",
  }),
  headerRight: css({
    justifyContent: "flex-end",
    display: "flex",
    flex: 1,
    gap: "var(--space-2)",
    alignItems: "center",
  }),
  titleWrapper: css({
    display: "flex",
    gap: "var(--space-2)",
    flexShrink: 1,
    overflow: "hidden",
    flexGrow: 1,
    flexBasis: "auto",
    alignItems: "center",
  }),
  titleDateWrapper: css({
    display: "flex",
    flexDirection: "column",
    width: "100%",
  }),
  contentWrapper: css({
    display: "flex",
    flexDirection: "column",
    padding: "var(--space-0)",
    alignItems: "flex-start",
    gap: "var(--space-2)",
    flex: 1,
  }),
  commentContent: css({ alignSelf: "stretch" }),
};
